/**
 * Performance monitoring utilities
 */

// Check if the Performance API is available
const hasPerformanceAPI =
  typeof performance !== "undefined" &&
  typeof performance.mark === "function" &&
  typeof performance.measure === "function";

/**
 * Create a performance marker at the current time
 * @param name Name of the marker
 */
export const mark = (name: string): void => {
  if (hasPerformanceAPI) {
    try {
      performance.mark(name);
    } catch (e) {
      console.warn("Failed to create performance marker:", e);
    }
  }
};

/**
 * Measure time between two markers
 * @param name Name of the measurement
 * @param startMark Starting marker name
 * @param endMark Ending marker name (optional, defaults to now)
 * @returns Duration in milliseconds, or undefined if measurement failed
 */
export const measure = (
  name: string,
  startMark: string,
  endMark?: string
): number | undefined => {
  if (hasPerformanceAPI) {
    try {
      const measurement = performance.measure(name, startMark, endMark);
      return measurement.duration;
    } catch (e) {
      console.warn(
        `Failed to measure performance from ${startMark} to ${
          endMark || "now"
        }:`,
        e
      );
    }
  }

  return undefined;
};

/**
 * Track execution time of a function
 * @param name Name for the performance measurement
 * @param fn Function to measure
 * @returns Result of the function
 */
export async function trackTime<T>(
  name: string,
  fn: () => Promise<T>
): Promise<T>;
export function trackTime<T>(name: string, fn: () => T): T;
export function trackTime<T>(
  name: string,
  fn: () => Promise<T> | T
): Promise<T> | T {
  const startMarkName = `${name}-start`;
  const endMarkName = `${name}-end`;

  mark(startMarkName);

  try {
    const result = fn();

    if (result instanceof Promise) {
      return result.finally(() => {
        mark(endMarkName);
        measure(name, startMarkName, endMarkName);
      });
    } else {
      mark(endMarkName);
      measure(name, startMarkName, endMarkName);
      return result;
    }
  } catch (error) {
    mark(endMarkName);
    measure(name, startMarkName, endMarkName);
    throw error;
  }
}

/**
 * Track API call performance
 * @param name Name of the API call to track
 * @param fn Function that makes the API call
 * @returns Result of the function
 */
export async function trackApiCall<T>(
  name: string,
  fn: () => Promise<T>
): Promise<T> {
  const start = Date.now();

  try {
    const result = await trackTime(`api-${name}`, fn);
    const duration = Date.now() - start;
    return result;
  } catch (error) {
    const duration = Date.now() - start;
    throw error;
  }
}

/**
 * Track user interaction performance
 * @param interactionType Type of interaction (click, submit, etc.)
 * @param fn Function to track
 * @returns Result of the function
 */
export async function trackUserInteraction<T>(
  interactionType: string,
  fn: () => Promise<T>
): Promise<T>;
export function trackUserInteraction<T>(
  interactionType: string,
  fn: () => T
): T;
export function trackUserInteraction<T>(
  interactionType: string,
  fn: () => Promise<T> | T
): Promise<T> | T {
  const start = Date.now();

  try {
    const result = trackTime(`interaction-${interactionType}`, fn);

    if (result instanceof Promise) {
      return result
        .then((value) => {
          const duration = Date.now() - start;
          return value;
        })
        .catch((error) => {
          const duration = Date.now() - start;
          throw error;
        });
    } else {
      const duration = Date.now() - start;
      return result;
    }
  } catch (error) {
    const duration = Date.now() - start;
    throw error;
  }
}

/**
 * Get performance metrics for recent operations
 * @returns Object with performance data
 */
export const getPerformanceMetrics = (): Record<string, number> => {
  if (!hasPerformanceAPI) {
    return {};
  }

  try {
    const entries = performance.getEntriesByType("measure");

    return entries.reduce((metrics, entry) => {
      metrics[entry.name] = entry.duration;
      return metrics;
    }, {} as Record<string, number>);
  } catch (e) {
    console.warn("Failed to get performance metrics:", e);
    return {};
  }
};

/**
 * Clear all performance marks and measures
 */
export const clearPerformanceMarks = (): void => {
  if (hasPerformanceAPI) {
    try {
      performance.clearMarks();
      performance.clearMeasures();
    } catch (e) {
      console.warn("Failed to clear performance marks:", e);
    }
  }
};
